import type { ZodiacSign } from '@repo/lib';
import type { NormalizedBirthData } from '@repo/lib/datetime-utils';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { OnboardingFormData as OnboardingSchemaData } from '../schemas/onboarding';

export type GuestOnboardingData = Partial<
  OnboardingSchemaData & {
    zodiacSign: ZodiacSign;
    birthDate?: string;
    normalizedBirthData?: NormalizedBirthData;
    birthPlaceTimezone?: string | null;
    schemaVersion?: number;
    completedAt?: string;
    updatedAt?: string;
    source?: 'onboarding' | 'migration' | 'manual';
  }
>;

// Constants for magic numbers
const MAX_MIGRATION_ATTEMPTS = 3;
export const GUEST_ONBOARDING_SCHEMA_VERSION = 2;

// GuestState type has been removed as it was unused

type GuestStore = {
  onboardingData: GuestOnboardingData | null;
  // Migration state management
  isMigrationMode: boolean;
  migrationBackup: GuestOnboardingData | null;
  migrationAttempts: number;
  // Actions
  setOnboardingData: (data: GuestOnboardingData) => void;
  clearGuestData: () => void;
  // Migration-specific actions
  enterMigrationMode: () => void;
  exitMigrationMode: () => void;
  backupGuestData: () => void;
  restoreGuestData: () => void;
  incrementMigrationAttempts: () => void;
  resetMigrationState: () => void;
  // Smart clearing with migration awareness
  clearGuestDataSafely: (force?: boolean) => void;
};

export const useGuestStore = create<GuestStore>()(
  persist(
    (set, get) => ({
      onboardingData: null,
      // Migration state
      isMigrationMode: false,
      migrationBackup: null,
      migrationAttempts: 0,

      setOnboardingData: (data) => {
        console.log('📝 [GUEST-STORE] Setting onboarding data:', data);
        set((state) => {
          const timestamp = new Date().toISOString();
          const nextData: GuestOnboardingData = {
            ...(state.onboardingData ?? {}),
            ...data,
            schemaVersion: GUEST_ONBOARDING_SCHEMA_VERSION,
            updatedAt: timestamp,
            completedAt: data.completedAt ?? state.onboardingData?.completedAt ?? timestamp,
            source: data.source ?? state.onboardingData?.source ?? 'onboarding',
          };

          if (
            nextData.normalizedBirthData &&
            nextData.birthTime &&
            !nextData.normalizedBirthData?.time
          ) {
            nextData.normalizedBirthData = {
              ...nextData.normalizedBirthData,
              time: `${nextData.birthTime}:00`,
            };
          }

          if (!nextData.birthPlaceTimezone && nextData.normalizedBirthData?.timezone) {
            nextData.birthPlaceTimezone = nextData.normalizedBirthData.timezone;
          }

          return { onboardingData: nextData };
        });
      },

      // Original clearGuestData for backward compatibility
      clearGuestData: () => {
        console.log('🔍 [GUEST-STORE] Guest data migration - clearing local storage');
        set({ onboardingData: null });
      },

      // Migration mode management
      enterMigrationMode: () => {
        console.log('🔄 [GUEST-STORE] Entering migration mode - protecting guest data');
        set({ isMigrationMode: true });
      },

      exitMigrationMode: () => {
        console.log('✅ [GUEST-STORE] Exiting migration mode');
        set({ isMigrationMode: false });
      },

      // Backup and restore mechanisms
      backupGuestData: () => {
        const { onboardingData } = get();
        if (onboardingData) {
          console.log('💾 [GUEST-STORE] Creating backup of guest data');
          set({ migrationBackup: onboardingData });
        }
      },

      restoreGuestData: () => {
        const { migrationBackup } = get();
        if (migrationBackup) {
          console.log('🔄 [GUEST-STORE] Restoring guest data from backup');
          set({
            onboardingData: migrationBackup,
            migrationBackup: null,
            isMigrationMode: false,
          });
        }
      },

      incrementMigrationAttempts: () => {
        const { migrationAttempts } = get();
        const newAttempts = migrationAttempts + 1;
        console.log(`🔄 [GUEST-STORE] Migration attempt ${newAttempts}`);
        set({ migrationAttempts: newAttempts });
      },

      resetMigrationState: () => {
        console.log('🔄 [GUEST-STORE] Resetting migration state');
        set({
          isMigrationMode: false,
          migrationBackup: null,
          migrationAttempts: 0,
        });
      },

      // Smart clearing with migration awareness
      clearGuestDataSafely: (force = false) => {
        const { isMigrationMode, migrationBackup, migrationAttempts } = get();

        // Don't clear if in migration mode unless forced
        if (isMigrationMode && !force) {
          console.log('⚠️ [GUEST-STORE] Skipping clear - migration mode active');
          return;
        }

        // Don't clear if we have a backup (unless forced or too many attempts)
        if (migrationBackup && !force && migrationAttempts < MAX_MIGRATION_ATTEMPTS) {
          console.log(
            '⚠️ [GUEST-STORE] Skipping clear - backup exists, attempts:',
            migrationAttempts
          );
          return;
        }

        console.log('🗑️ [GUEST-STORE] Safely clearing guest data');
        set({
          onboardingData: null,
          // Reset migration state when clearing
          isMigrationMode: false,
          migrationBackup: null,
          migrationAttempts: 0,
        });
      },
    }),
    {
      name: 'guest-local-storage',
      storage: createJSONStorage(() => localStorage),
      // Add migration state to partialize to avoid persistence issues
      partialize: (state) => ({
        onboardingData: state.onboardingData,
        isMigrationMode: state.isMigrationMode,
        migrationBackup: state.migrationBackup,
        migrationAttempts: state.migrationAttempts,
      }),
    }
  )
);
